import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Users, Briefcase, Fuel, ShieldCheck, Check, X, ChevronLeft, ChevronRight, Phone } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (url) => (url?.startsWith("http") ? url : `${BASE}${url}`);

const SpecRow = ({ label, value }) => (
  <div className="flex justify-between gap-3 py-1.5 border-b border-dashed border-[#eef2ee] last:border-0">
    <span className="text-[11px] uppercase tracking-wide text-[#57685e]">{label}</span>
    <span className="text-xs font-semibold text-[#0F1E17] text-right">{value}</span>
  </div>
);

export const VehicleDetailModal = ({ vehicle: v, gallery, onClose, onSelect }) => {
  const { t } = useTranslation();
  const [idx, setIdx] = useState(0);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setIdx(0);
    setExpanded(false);
    const esc = (e) => e.key === "Escape" && onClose();
    if (v) {
      document.addEventListener("keydown", esc);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", esc);
      document.body.style.overflow = "";
    };
  }, [v, onClose]);

  if (!v) return null;
  const images = gallery?.length ? gallery : [{ image_id: "hero", url: v.image, label: "Front" }];
  const img = images[Math.min(idx, images.length - 1)];
  const std = v.insurance_tier === "standard";
  const specs = v.specs || {};
  const isHybrid = (v.fuel || "").toLowerCase().includes("hybrid");

  return (
    <div className="fixed inset-0 z-[70] flex items-start sm:items-center justify-center p-3 sm:p-6" data-testid={`vehicle-detail-modal-${v.id}`}>
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-[#0F1E17]/60 backdrop-blur-sm cursor-default" data-testid="vehicle-detail-backdrop" />
      <div className="relative bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl">
        {/* Green header */}
        <div className="bg-[#144733] text-white px-5 py-4 rounded-t-2xl sticky top-0 z-10 flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="bg-[#009900] text-white text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-semibold">{t("Group")} {v.group}</span>
              <span className="text-[10px] font-mono tracking-widest text-white/60">{v.acriss}</span>
            </div>
            <h2 className="text-lg font-bold mt-1" data-testid="vehicle-detail-name">{v.name} <span className="text-xs font-normal text-white/70">· {v.group_label} · {t("or similar")}</span></h2>
          </div>
          <button type="button" onClick={onClose} className="text-white/70 hover:text-white p-1" data-testid="vehicle-detail-close"><X className="w-5 h-5" /></button>
        </div>

        {/* Rate strip */}
        <div className="grid grid-cols-3 divide-x divide-[#e2ebe4] border-b border-[#e2ebe4] bg-[#fafcfa]" data-testid="vehicle-detail-rates">
          {[["Daily", v.daily_incl, "/day"], ["Weekly", v.weekly_incl, "/week"], ["Monthly", v.monthly_incl, "/month"]].map(([l, amt, per]) => (
            <div key={l} className="px-4 py-3 text-center">
              <div className="text-[10px] uppercase tracking-widest text-[#57685e]">{t(l)}</div>
              <div className="text-sm font-bold text-[#0F1E17]">{amt ? `AED ${amt.toLocaleString()}` : t("On request")}<span className="text-[10px] font-normal text-[#8a978f]">{amt ? t(per) : ""}</span></div>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-[#8a978f] text-center py-1.5 border-b border-[#f1f3f1]">{t("Indicative rates incl. 5% VAT · monthly includes 3,000 km")}</p>

        {/* Gallery */}
        <div className={`relative bg-[#f4f7f4] ${expanded ? "h-80" : "h-56"} transition-all`}>
          <img src={imgSrc(img.url)} alt={`${v.name} — ${img.label}`} className="w-full h-full object-cover" />
          {images.length > 1 && (
            <>
              <button type="button" onClick={() => setIdx((idx - 1 + images.length) % images.length)}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/85 rounded-full p-1.5 hover:bg-white" data-testid="vehicle-detail-prev"><ChevronLeft className="w-4 h-4 text-[#144733]" /></button>
              <button type="button" onClick={() => setIdx((idx + 1) % images.length)}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/85 rounded-full p-1.5 hover:bg-white" data-testid="vehicle-detail-next"><ChevronRight className="w-4 h-4 text-[#144733]" /></button>
            </>
          )}
          <button type="button" onClick={() => setExpanded(!expanded)}
            className="absolute bottom-2 right-2 bg-[#0F1E17]/75 text-white text-[10px] uppercase tracking-wide px-2.5 py-1.5 rounded hover:bg-[#0F1E17]" data-testid="vehicle-detail-expand">
            {expanded ? t("Shrink") : t("Expand")}
          </button>
          <span className="absolute bottom-2 left-2 bg-white/85 text-[#144733] text-[10px] px-2 py-1 rounded">{img.label} · {idx + 1}/{images.length}</span>
        </div>
        {images.length > 1 && (
          <div className="flex gap-2 px-5 pt-3 overflow-x-auto">
            {images.map((im, i) => (
              <button key={im.image_id} type="button" onClick={() => setIdx(i)}
                className={`shrink-0 w-16 h-12 rounded overflow-hidden border-2 ${i === idx ? "border-[#009900]" : "border-transparent opacity-70"}`}>
                <img src={imgSrc(im.url)} alt={im.label} className="w-full h-full object-cover" loading="lazy" />
              </button>
            ))}
          </div>
        )}

        <div className="p-5 space-y-5">
          {/* Quick stats */}
          <div className="grid grid-cols-3 gap-3 text-center" data-testid="vehicle-detail-stats">
            {[[Users, t("{{n}} Passengers", { n: v.seats })], [Briefcase, t("{{lg}} Large + {{sm}} Small bags", { lg: v.luggage_large, sm: v.luggage_small })], [Fuel, v.fuel]].map(([Icon, label]) => (
              <div key={label} className="bg-[#fafcfa] border border-[#e2ebe4] rounded-xl py-4 px-2 flex flex-col items-center justify-center gap-1.5">
                <Icon className="w-5 h-5 text-[#009900]" />
                <div className="text-[11px] font-medium text-[#41504a] leading-tight">{label}</div>
              </div>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            {/* Specifications */}
            <div>
              <h3 className="heading-upper text-xs tracking-widest text-[#144733] mb-2">{t("Vehicle specifications")}</h3>
              <div data-testid="vehicle-detail-specs">
                {specs.horsepower && <SpecRow label={t("Horsepower")} value={specs.horsepower} />}
                {specs.fuel_economy && <SpecRow label={t("Fuel consumption")} value={specs.fuel_economy} />}
                {specs.co2 && <SpecRow label={t("CO₂ emissions")} value={specs.co2} />}
                {specs.boot_capacity && <SpecRow label={t("Boot capacity")} value={specs.boot_capacity} />}
                {isHybrid && specs.est_range && <SpecRow label={t("Estimated range")} value={specs.est_range} />}
                <SpecRow label={t("Type")} value={v.group_label} />
                <SpecRow label={t("Rental group")} value={t("Group {{g}} — {{desc}}", { g: v.group, desc: v.group_desc || v.group_label })} />
              </div>
            </div>
            {/* Amenities */}
            <div>
              <h3 className="heading-upper text-xs tracking-widest text-[#144733] mb-2">{t("Included comfort & safety amenities")}</h3>
              <ul className="space-y-1.5" data-testid="vehicle-detail-amenities">
                {v.features.map((f) => (
                  <li key={f} className="flex gap-2 text-xs text-[#41504a]"><Check className="w-3.5 h-3.5 text-[#009900] shrink-0 mt-0.5" />{f}</li>
                ))}
              </ul>
            </div>
          </div>

          {v.best_for && (
            <div className="bg-[#f0f9f0] border border-[#009900]/20 rounded-xl p-4" data-testid="vehicle-detail-bestfor">
              <h3 className="heading-upper text-xs tracking-widest text-[#144733] mb-1">{t("Best suited for")}</h3>
              <p className="text-xs text-[#41504a] leading-relaxed">{v.best_for}</p>
            </div>
          )}

          {/* Security hold & deposit */}
          <div className="bg-[#fafcfa] border border-[#e2ebe4] rounded-xl p-4" data-testid="vehicle-detail-deposit">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-4 h-4 text-[#009900]" />
              <h3 className="heading-upper text-xs tracking-widest text-[#144733]">{t("Security hold & deposit")}</h3>
              <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold ${std ? "bg-[#e2ebe4] text-[#144733]" : "bg-[#144733] text-[#FFDC00]"}`}>{std ? t("Standard") : t("Premium")}</span>
            </div>
            <p className="text-xs text-[#41504a] leading-relaxed">
              {t("Credit card hold AED {{cc}} (pre-auth, released 21–28 days after return) or debit-card deposit AED {{dc}}. Excess AED {{excess}} · Minimum driver age {{age}}.", { cc: v.deposit_cc.toLocaleString(), dc: v.deposit_dc.toLocaleString(), excess: v.excess.toLocaleString(), age: v.min_age })}
            </p>
            <p className="text-xs text-[#009900] font-semibold mt-1.5">{t("Prefer no hold at all? Choose the No-Deposit Waiver at booking — nothing blocked on your card.")}</p>
          </div>

          {onSelect && (
            <button type="button" onClick={() => onSelect(v)} className="btn-cta rounded w-full py-3 text-sm" data-testid="vehicle-detail-select-btn">
              {t("Select this car")}
            </button>
          )}
          <p className="text-[10px] text-[#8a978f] text-center flex items-center justify-center gap-1.5">
            <Phone className="w-3 h-3" /> {t("Fleet Guide is for reference — confirm live rates at booking, or call 800 EUROPCAR (800 38767227)")}
          </p>
        </div>
      </div>
    </div>
  );
};
